import { Routes, Route, Navigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import AppLayout from '@/components/layout/AppLayout'
import ErrorBoundary from '@/components/common/ErrorBoundary'
import LoginPage from '@/pages/LoginPage'
import AuthCallbackPage from '@/pages/AuthCallbackPage'
import DashboardPage from '@/pages/DashboardPage'
import AgentsPage from '@/pages/AgentsPage'
import AgentDetailPage from '@/pages/AgentDetailPage'
import AgentExecutePage from '@/pages/AgentExecutePage'
import WorkflowsPage from '@/pages/WorkflowsPage'
import WorkflowEditorPage from '@/pages/WorkflowEditorPage'
import WorkflowExecutionPage from '@/pages/WorkflowExecutionPage'
import ZiroSystemWorkflowPage from '@/pages/ZiroSystemWorkflowPage'
import ExecutionsPage from '@/pages/ExecutionsPage'
import TeamsPage from '@/pages/TeamsPage'
import TeamEditorPage from '@/pages/TeamEditorPage'
import TeamDetailPage from '@/pages/TeamDetailPage'
import TeamExecutionPage from '@/pages/TeamExecutionPage'
import GraphEditorPage from '@/pages/GraphEditorPage'
import GraphViewerPage from '@/pages/GraphViewerPage'
import MCPServersPage from '@/pages/MCPServersPage'
import SettingsPage from '@/pages/SettingsPage'
import HPCJobsPage from '@/pages/HPCJobsPage'
import HPCJobDetailPage from '@/pages/HPCJobDetailPage'
import ProjectInboxPage from '@/pages/ProjectInboxPage'
import PlaygroundPage from '@/pages/PlaygroundPage'
import UnifiedExecutionPage from '@/pages/UnifiedExecutionPage'
import AgentArchitectPage from '@/pages/AgentArchitectPage'
import SystemDashboardPage from '@/pages/SystemDashboardPage'
import DataPage from '@/pages/DataPage'
import ArgusObserverPage from '@/pages/ArgusObserverPage'
import CrewsPage from '@/pages/CrewsPage'
import CrewDetailPage from '@/pages/CrewDetailPage'
import CrewExecutePage from '@/pages/CrewExecutePage'
import DocsPage from '@/pages/DocsPage'
import AuditsPage from '@/pages/AuditsPage'
import PIIProxyPage from '@/pages/PIIProxyPage'
import ResearchPage from '@/pages/ResearchPage'
import EditorialPage from '@/pages/EditorialPage'
import SimulationDashboardPage from '@/pages/SimulationDashboardPage'
import SimulationScenariosPage from '@/pages/SimulationScenariosPage'
import SimulationRunPage from '@/pages/SimulationRunPage'
import SimulationReplayPage from '@/pages/SimulationReplayPage'
import ConnectedServicesPage from '@/pages/ConnectedServicesPage'
import WYSIWYGDashboardPage from '@/pages/WYSIWYGDashboardPage'
import WYSIWYGExecutionPage from '@/pages/WYSIWYGExecutionPage'
import AgentTeamsPage from '@/pages/AgentTeamsPage'
import OperationsPage from '@/pages/OperationsPage'
import SimulationHubPage from '@/pages/SimulationHubPage'
import SimulationAnalyticsPage from '@/pages/SimulationAnalyticsPage'
import ProtectedRoute from '@/components/common/ProtectedRoute'

const App = observer(() => {
  return (
    <ErrorBoundary>
      <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="agents" element={<AgentsPage />} />
        <Route path="agents/:id" element={<AgentDetailPage />} />
        <Route path="agents/:id/execute" element={<AgentExecutePage />} />
        <Route path="agent-teams" element={<AgentTeamsPage />} />
        <Route path="operations" element={<OperationsPage />} />
        <Route path="crews" element={<CrewsPage />} />
        <Route path="crews/:name" element={<CrewDetailPage />} />
        <Route path="crews/:name/execute" element={<CrewExecutePage />} />
        <Route path="teams" element={<TeamsPage />} />
        <Route path="teams/new" element={<TeamEditorPage />} />
        <Route path="teams/:id" element={<TeamDetailPage />} />
        <Route path="teams/:id/edit" element={<TeamEditorPage />} />
        <Route path="teams/:id/execute" element={<TeamExecutionPage />} />
        <Route path="data" element={<DataPage />} />
        <Route path="data/graphs" element={<DataPage tab="graphs" />} />
        <Route path="data/collections" element={<DataPage tab="collections" />} />
        <Route path="data/memory" element={<DataPage tab="memory" />} />
        <Route path="data/writes" element={<DataPage tab="writes" />} />
        <Route path="data/pii-sim" element={<DataPage tab="pii-sim" />} />
        <Route path="data/neuralgraph" element={<DataPage tab="neuralgraph" />} />
        <Route path="knowledge-graphs" element={<Navigate to="/data/graphs" replace />} />
        <Route path="knowledge-graphs/new" element={<GraphEditorPage />} />
        <Route path="knowledge-graphs/:graphId" element={<GraphViewerPage />} />
        <Route path="knowledge-graphs/:graphId/edit" element={<GraphEditorPage />} />
        <Route path="workflows" element={<WorkflowsPage />} />
        <Route path="workflows/new" element={<WorkflowEditorPage />} />
        <Route path="workflows/ziro-system" element={<ZiroSystemWorkflowPage />} />
        <Route path="workflows/:id" element={<WorkflowEditorPage />} />
        <Route path="workflows/:id/execute" element={<WorkflowExecutionPage />} />
        <Route path="executions" element={<ExecutionsPage />} />
        <Route path="audits" element={<AuditsPage />} />
        {/* Redirects for old routes */}
        <Route path="knowledge" element={<Navigate to="/data/collections" replace />} />
        <Route path="memory" element={<Navigate to="/data/memory" replace />} />
        <Route path="mcp" element={<MCPServersPage />} />
        <Route path="hpc" element={<HPCJobsPage />} />
        <Route path="hpc/jobs/:jobId" element={<HPCJobDetailPage />} />
        <Route path="inbox" element={<ProjectInboxPage />} />
        <Route path="inbox/:projectId" element={<ProjectInboxPage />} />
        <Route path="playground" element={<PlaygroundPage />} />
        <Route path="unified" element={<UnifiedExecutionPage />} />
        <Route path="architect" element={<AgentArchitectPage />} />
        <Route path="system" element={<SystemDashboardPage />} />
        <Route path="argus" element={<ArgusObserverPage />} />
        <Route path="pii-proxy" element={<PIIProxyPage />} />
        <Route path="research" element={<ResearchPage />} />
        <Route path="editorial" element={<EditorialPage />} />
        <Route path="simulation" element={<SimulationHubPage />} />
        <Route path="simulation/scenarios" element={<SimulationHubPage />} />
        <Route path="simulation/analytics" element={<SimulationHubPage />} />
        <Route path="simulation/runs/:id" element={<SimulationRunPage />} />
        <Route path="simulation/runs/:id/replay" element={<SimulationReplayPage />} />
        <Route path="wysiwyg" element={<WYSIWYGDashboardPage />} />
        <Route path="wysiwyg/executions/:id" element={<WYSIWYGExecutionPage />} />
        <Route path="docs" element={<DocsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/connected-services" element={<ConnectedServicesPage />} />
      </Route>

      {/* No wildcard catch-all — letting unmatched URLs fall through
          to ProtectedRoute keeps the auth gate honest. With the old
          ``<Route path="*" element={<Navigate to="/dashboard">} />``
          rule, an unauthenticated user opening the app at "/" would
          be redirected to /dashboard *outside* the ProtectedRoute
          branch, bypassing the isAuthenticated check and stranding
          them on a dashboard whose API calls all 401 from the real
          backend (visible in ZBox preview iframes where every session
          starts with empty localStorage). Removed 2026-04-28. */}
    </Routes>
    </ErrorBoundary>
  )
})

export default App
